import React, { useRef } from 'react';
import { useDrag, useDrop, XYCoord } from 'react-dnd';
import { Box } from '@ghq-abi/design-system';
import type { Identifier } from 'dnd-core';

import { DraggableProps } from './types';

export function Draggable<T extends { index: number }>({
  item,
  children,
  handleSortableSuccessor,
}: DraggableProps<T>) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    T & { index: number },
    void,
    { handlerId: Identifier | null }
  >({
    accept: item.type,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(itemHover: T, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = itemHover.index;
      const hoverIndex = item.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      handleSortableSuccessor(itemHover, item);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      // eslint-disable-next-line no-param-reassign
      itemHover.index = hoverIndex;
    },
  });

  const [{ isDragging }, dragRef] = useDrag({
    type: item.type,
    item,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [, drag] = useDrag({
    type: 'content',
    item,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(dragRef(drop(ref)));
  return (
    <Box
      ref={ref}
      css={{
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      data-handler-id={handlerId}
    >
      {children}
    </Box>
  );
}
